import { useEffect, useRef } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import TableDragDivider from "@FillInTableContainer/shared/components/TableDragDivider/TableDragDivider";
import useCheckIsDisplayGroupShellDragDivider from "@FillInTableContainer/shared/hooks/useCheckIsDisplayGroupShellDragDivider";
import { useHandleTableCreatorGroupShellDrag } from "@FillInTableContainer/shared/hooks/useHandleTableCreatorGroupShellDrag";
import { TableCreatorSubGroupGroupShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getGroupShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import DealLaScFinancialTermsGroupShell from "./components/DealLaScFinancialTermsGroupShell/DealLaScFinancialTermsGroupShell";
import * as S from "./DealLaScFinancialTermsGroupShellSection.style";
import useDealLaScFinancialTermsTableCreatorGroupShellSection from "./hooks/useDealLaScFinancialTermsTableCreatorGroupShellSection";

interface Props {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellSectionKey: string | null;
  groupShellList: TableCreatorSubGroupGroupShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellSectionIndex: number;
}

const ARTICLE_SECTION = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

const isDraggingGroupShellKey = "Main Financial Terms";

const DealTermsLicenseAgreementGroupShellSection = ({
  groupShellSectionIndex,
  groupShellSectionKey,
  groupTableIndex,
  groupTableKey,
  groupShellList,
  tableIndex,
  tableKey,
}: Props) => {
  const groupShellSectionProps = {
    groupShellSectionIndex,
    groupShellSectionKey,
    groupTableIndex,
    groupTableKey,
    tableIndex,
    tableKey,
  };

  const {
    focusStatus,
    numberOfGroupShellCount,
    handleClickToClearGroupShellSection,
    handleDefaultFocus,
    isGroupShellSectionFocus,
    isClearGroupShellSection,
    isDeleteGroupShellSection,
  } = useDealLaScFinancialTermsTableCreatorGroupShellSection(
    groupShellSectionProps,
  );

  const {
    handleGroupShellDragEnd,
    handleGroupShellDragOn,
    handleGroupShellDragOver,
    handleGroupShellDragStart,
    groupShellDraggedIndexInfo,
    isDraggingGroupShell,
  } = useHandleTableCreatorGroupShellDrag(ARTICLE_SECTION, tableIndex);

  const { isLastGroupShell, handleCheckDragDividerDisplay } =
    useCheckIsDisplayGroupShellDragDivider(
      tableIndex,
      isDraggingGroupShell,
      isDraggingGroupShellKey,
      ARTICLE_SECTION,
    );

  const titleRef = useRef<HTMLDivElement>(null);
  const clearButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleCheckClickOutside = (e: MouseEvent) => {
      if (isGroupShellSectionFocus) {
        const target = e.target as HTMLElement;
        const clickSelectButton = clearButtonRef.current?.contains(target);
        const clickGroupShellTitle = titleRef.current?.contains(target);

        if (clickSelectButton || clickGroupShellTitle) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutside);

    return () => window.removeEventListener("click", handleCheckClickOutside);
  }, [handleDefaultFocus, isGroupShellSectionFocus]);

  return (
    <S.GroupShellSectionWrapper
      key={`groupShellList-${String(groupShellSectionIndex)}`}
      isFocused={isGroupShellSectionFocus}
    >
      {(isClearGroupShellSection || isDeleteGroupShellSection) && (
        <S.FixedDeleteButtonWrapper
          isShellTitle={
            !!getGroupShellTitle(ARTICLE_SECTION, groupShellSectionKey)
          }
          ref={clearButtonRef}
        >
          <S.DeleteGroupShellSectionButtonWrapper>
            <S.DeleteGroupShellSectionButton
              data-button="delete-button"
              onClick={handleClickToClearGroupShellSection}
            >
              <img src={IconXMark} alt="button for clear Row" />
              {focusStatus.type === "clear" ? `Clear` : null}
              {focusStatus.type === "delete" ? `Delete` : null}
            </S.DeleteGroupShellSectionButton>
          </S.DeleteGroupShellSectionButtonWrapper>
        </S.FixedDeleteButtonWrapper>
      )}
      {groupShellList.map(({ groupShellKey, subGroupShellList }) => (
        <>
          {newArrayToCountValue(numberOfGroupShellCount(groupShellKey)).map(
            (_, groupShellIndex) => (
              <S.DraggableGroupShellWrapper
                isTargetDraggingTable={
                  isDraggingGroupShell &&
                  groupShellDraggedIndexInfo[0] !== groupShellIndex
                }
                onDragOver={(e) => handleGroupShellDragOver(e, groupShellIndex)}
              >
                {isAddDeleteGroupShell(ARTICLE_SECTION, groupShellKey) && (
                  <TableDragDivider
                    isDraggingEndPosition={handleCheckDragDividerDisplay({
                      moveDragIndex: groupShellDraggedIndexInfo[1],
                      startDragIndex: groupShellDraggedIndexInfo[0],
                      groupShellIndex,
                      groupShellKey,
                    })}
                  />
                )}
                <DealLaScFinancialTermsGroupShell
                  key={`group-shell-${String(groupTableIndex)}-${String(
                    tableIndex,
                  )}-${String(groupShellIndex)}`}
                  subGroupShellList={subGroupShellList}
                  groupTableKey={groupTableKey}
                  groupTableIndex={groupTableIndex}
                  groupShellSectionIndex={groupShellSectionIndex}
                  groupShellSectionKey={groupShellSectionKey}
                  tableKey={tableKey}
                  tableIndex={tableIndex}
                  groupShellKey={groupShellKey}
                  groupShellIndex={groupShellIndex}
                  groupShellCount={numberOfGroupShellCount(groupShellKey)}
                  handleDragEnd={handleGroupShellDragEnd}
                  handleDragOn={handleGroupShellDragOn}
                  handleDragStart={handleGroupShellDragStart}
                  isDraggableGroupShell={isDraggingGroupShell}
                  isFocusGroupShellContainer={isGroupShellSectionFocus}
                />
                {isAddDeleteGroupShell(ARTICLE_SECTION, groupShellKey) &&
                  isLastGroupShell(groupShellIndex, groupShellKey) && (
                    <TableDragDivider
                      isDraggingEndPosition={handleCheckDragDividerDisplay({
                        moveDragIndex: groupShellDraggedIndexInfo[1],
                        startDragIndex: groupShellDraggedIndexInfo[0],
                        groupShellIndex,
                        groupShellKey,
                        lastIndex: newArrayToCountValue(
                          numberOfGroupShellCount(groupShellKey),
                        ).length,
                      })}
                    />
                  )}
              </S.DraggableGroupShellWrapper>
            ),
          )}
        </>
      ))}
    </S.GroupShellSectionWrapper>
  );
};

export default DealTermsLicenseAgreementGroupShellSection;
